import type { Step } from "shared/components/common/multiStepForm";
import { StepOneVS, StepTwoVS } from "shared/utils/validation";
import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Icons, Images } from "assets";
import {
  GetClientOnboardingProjectService,
  ProjectOnboardingService,
} from "shared/services/project";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { routeConstant } from "shared/routes/routeConstant";
import classNames from "classnames";
import MultiStepForm from "shared/components/common/multiStepForm";
import StepOne from "shared/components/auth/stepOne";
import StepTwo from "shared/components/auth/stepTwo";
import styles from "./style.module.scss";
import Success from "shared/components/auth/success";
import CustomButton from "shared/components/common/customButton";
import Spinner from "shared/components/common/spinner";
import { ONBOARDING_DISMISSED_KEY } from "shared/utils/pendingOnboarding";
import { appendCollaboratorInvites } from "shared/utils/collaboratorAccess";
import { resolveOnboardingEntry } from "./resolveOnboardingEntry";



const OnboardingFlow = () => {
  const auth = useSelector((state: any) => state.root.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectLoading, setIsProjectLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnboardingProject = async () => {
      try {
        const response = await GetClientOnboardingProjectService();
        const raw = response.data?.data ?? response.data;
        if (cancelled) return;
        // Pick the project requested via navigation state, else the first still pending.
        const requestedId = (
          location.state as { projectId?: number } | null
        )?.projectId;
        const { selected, pending, shouldLeave } = resolveOnboardingEntry({
          list: raw,
          requestedId,
        });
        dispatch(
          setAuthReducer({
            onboarding_projects: pending,
            onboarding_project: selected,
          }),
        );
        if (shouldLeave) {
          // The project was deleted or already onboarded, so do not strand the client on a blank form.
          sessionStorage.setItem(ONBOARDING_DISMISSED_KEY, "1");
          navigate(routeConstant.client.projects.path, { replace: true });
          return;
        }
      } catch (err) {
        if (!cancelled) handleErrors(err);
      } finally {
        if (!cancelled) setIsProjectLoading(false);
      }
    };
    fetchOnboardingProject();
    return () => {
      cancelled = true;
    };
  }, [dispatch, location.state, navigate]);

  const handleBackToProjects = () => {
    sessionStorage.setItem(ONBOARDING_DISMISSED_KEY, "1");
    navigate(routeConstant.client.projects.path, { replace: true });
  };

  const steps: Step[] = [
    { id: 1, label: "Basic Info" },
    { id: 2, label: "Inspiration" },
  ];

  const updateData = useCallback((data: any) => {
    setFormData((prev: any) => ({
      ...prev,
      ...data,
    }));
  }, []);

  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev };
      if ((formData?.currentAddress ?? "").trim()) {
        delete next.currentAddress;
      }
      if ((formData?.billingAddress ?? "").trim()) {
        delete next.billingAddress;
      }
      return next;
    });
  }, [formData?.currentAddress, formData?.billingAddress]);

  useEffect(() => {
    const items = formData?.imageItems;
    if (!Array.isArray(items)) return;
    setErrors((prev) => {
      const next = { ...prev };
      const hasAtLeastOneImage = items.some(
        (item: any) => item?.hasImage || item?.file || item?.imageUrl,
      );
      if (hasAtLeastOneImage) {
        delete next.imageItems;
      }
      items.forEach((item: any) => {
        const id = item?.id;
        if (!id) return;
        if (item?.hasImage || item?.file || item?.imageUrl) {
          delete next[`image-${id}`];
        }
        if ((item?.imageLabel ?? "").trim()) {
          delete next[`imageLabel-${id}`];
        }
        if ((item?.notes ?? "").trim()) {
          delete next[`notes-${id}`];
        }
      });
      return next;
    });
  }, [formData?.imageItems]);

  const handleNext = async () => {
    if (currentStep === 1) {
      try {
        await StepOneVS.validate(formData, { abortEarly: false });
        setErrors({});
        setCurrentStep(2);

        localStorage.setItem(
          "onboardingProgress",
          JSON.stringify({ step: 2, formData }),
        );
      } catch (err: any) {
        const formattedErrors: Record<string, string> = {};
        err.inner.forEach((e: any) => {
          if (e.path) formattedErrors[e.path] = e.message;
        });
        setErrors(formattedErrors);
      }
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const transformFormDataToPayload = (data: any) => {
    const formData = new FormData();

    formData.append("client_current_address", data.currentAddress || "");
    formData.append("client_billing_address", data.billingAddress || "");

    if (Array.isArray(data.collaborator_ids)) {
      data.collaborator_ids.forEach((id: number) => {
        formData.append("collaborator_ids[]", String(id));
      });
    }

    if (Array.isArray(data.collaborator_view_only_ids)) {
      data.collaborator_view_only_ids.forEach((id: number) => {
        formData.append("collaborator_view_only_ids[]", String(id));
      });
    }

    if (Array.isArray(data.imageItems)) {
      data.imageItems.forEach((item: any, index: number) => {
        formData.append(`inspirations[${index}][label]`, item.imageLabel || "");

        formData.append(
          `inspirations[${index}][additional_notes]`,
          item.notes || "",
        );

        if (item.file instanceof File) {
          formData.append("inspirations", item.file);
        }
      });
    }

    if (Array.isArray(data.collaborators)) {
      appendCollaboratorInvites(formData, data.collaborators);
    }

    return formData;
  };

  const handleSubmit = async () => {
    try {
      await StepTwoVS.validate(formData, { abortEarly: false });
      setErrors({});

      const project_id = auth?.onboarding_project?.project_id;
      if (!project_id) {
        toastMessage("error", "Project ID not found. Please try again.");
        return;
      }

      setIsLoading(true);

      const payload = await transformFormDataToPayload(formData);

      await ProjectOnboardingService(project_id, payload);

      sessionStorage.removeItem(ONBOARDING_DISMISSED_KEY);
      localStorage.removeItem("onboardingProgress");

      const completedName = auth?.onboarding_project?.name || "your project";

      // The submitted project has moved out of ONBOARDING status. Re-check
      // whether the client still has another project awaiting onboarding.
      let remaining: any[] = [];
      try {
        const response = await GetClientOnboardingProjectService();
        const raw = response.data?.data ?? response.data;
        remaining = (Array.isArray(raw) ? raw : []).filter(
          (p: any) =>
            p &&
            typeof p === "object" &&
            p.project_id !== project_id &&
            (p.status || "").toLowerCase() === "onboarding",
        );
      } catch {
        // If the re-check fails, fall back to finishing the flow.
        remaining = [];
      }

      setIsLoading(false);

      if (remaining.length > 0) {
        // Loop: set up the next project that still needs onboarding.
        const next = remaining[0];
        dispatch(
          setAuthReducer({
            onboarding_projects: remaining,
            onboarding_project: next,
          }),
        );
        setFormData({});
        setErrors({});
        setCurrentStep(1);
        toastMessage(
          "success",
          `Onboarding for ${completedName} complete! Let's set up your next project.`,
        );
      } else {
        // No more projects need onboarding — finish.
        dispatch(
          setAuthReducer({
            onboarding_projects: [],
            onboarding_project: null,
          }),
        );
        setIsSuccess(true);
        toastMessage("success", "Onboarding completed successfully!");
      }
    } catch (err: any) {
      setIsLoading(false);

      if (err.inner) {
        const formattedErrors: Record<string, string> = {};

        err.inner.forEach((e: any) => {
          if (e.path) {
            const match = e.path.match(/imageItems\[(\d+)\]\.(.*)/);
            const itemMatch =
              e.path.match(/imageItems\.(\d+)$/) ||
              e.path.match(/imageItems\[(\d+)\]$/);
            if (match) {
              const [, index, field] = match;
              const id = formData.imageItems?.[index]?.id;
              formattedErrors[`${field}-${id}`] = e.message;
            } else if (itemMatch) {
              const index = parseInt(itemMatch[1], 10);
              const id = formData.imageItems?.[index]?.id;
              if (id) formattedErrors[`image-${id}`] = e.message;
            } else if (e.path === "imageItems") {
              formattedErrors.imageItems = e.message;
            }
          }
        });

        setErrors(formattedErrors);
      } else {
        handleErrors(err);
      }
    }
  };

  return (
    <div className={styles.customContainer}>
      <Icons.Logo className={classNames(styles.logo, "")} />
      {!isSuccess && (
        <div className={styles.backToProjectsWrapper}>
          <CustomButton
            title="Back to Projects"
            onClick={handleBackToProjects}
            containerStyle={classNames(styles.btnPrevious, "")}
            titleStyle={styles.title}
            Icon={Icons.ChevLeft}
            IconDirection="left"
            type="button"
            iconStyle={styles.chevIcon}
          />
        </div>
      )}
      <div className="flex flex-col sm:p-4 relative w-full">
        {isSuccess ? (
          <Success />
        ) : (
          <div className="">
            {currentStep === 1 && (
              <div className={classNames(styles.main, "flex flex-col  gap-3")}>
                <img src={Images.HomeImg} alt="Home Icon" />

                <div
                  className={classNames(
                    styles.description,
                    "flex flex-col gap-3",
                  )}
                >
                  <h1>Start Your Design Journey!</h1>
                  <p>
                    We’re excited to start designing your project! Before we
                    begin, please take a few minutes to complete this short
                    questionnaire.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className={classNames(styles.main, "flex flex-col  gap-3")}>
                <div className={classNames(styles.description, "")}>
                  <h3>Add Inspiration</h3>
                </div>
              </div>
            )}

            <MultiStepForm
              steps={steps}
              currentStep={currentStep}
              showStepNumbers={true}
            >
              {currentStep === 1 &&
                (isProjectLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : (
                  <StepOne
                    data={formData}
                    updateData={updateData}
                    errors={errors}
                    onNext={handleNext}
                    isFirstStep={true}
                    isLastStep={false}
                  />
                ))}

              {currentStep === 2 && (
                <StepTwo
                  data={formData}
                  updateData={updateData}
                  errors={errors}
                  onNext={handleSubmit}
                  onPrev={handlePrev}
                  isFirstStep={false}
                  isLastStep={true}
                />
              )}
            </MultiStepForm>
          </div>
        )}

        {!isSuccess && (
          <div className={styles.navigationSection}>
            <div className={styles.prevButtonWrapper}>
              {currentStep > 1 && (
                <CustomButton
                  title="Back"
                  onClick={handlePrev}
                  containerStyle={classNames(styles.btnPrevious, "")}
                  titleStyle={styles.title}
                  Icon={Icons.ChevLeft}
                  IconDirection="left"
                  type="button"
                  iconStyle={styles.chevIcon}
                />
              )}
            </div>

            <div className={styles.nextButtonWrapper}>
              {currentStep < steps.length ? (
                <CustomButton
                  title="Next"
                  onClick={handleNext}
                  containerStyle={styles.btnNext}
                  type="button"
                  Icon={Icons.ChevRight}
                  IconDirection="right"
                  iconStyle={styles.chevIcon}
                />
              ) : (
                <CustomButton
                  title="Next"
                  onClick={handleSubmit}
                  containerStyle={styles.btnNext}
                  type="button"
                  Icon={Icons.ChevRight}
                  IconDirection="right"
                  iconStyle={styles.chevIcon}
                  loading={isLoading}
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
